import React from "react";
import {
  Header,
  ButtonFilled,
  Body,
} from "../../../../styleguide/atoms/index.js";
import {
  ButtonsWrapper,
  WinnerContainer,
  WinnerWrapper,
  WinnerList,
} from "../../styles/Game.styles";
import Donations from "../Donations.js";

function joinList(items = []) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function nameWithRole(p) {
  const name = p?.name || "Unknown";
  const role = p?.roleTitle || "";
  return role ? `${name} (${role})` : `"${name}"`;
}

function toMs(t) {
  if (!t) return null;
  if (typeof t === "number") return t;
  if (typeof t === "string") {
    const n = Date.parse(t);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof t === "object") {
    if (typeof t.toMillis === "function") return t.toMillis();
    if (typeof t.seconds === "number") {
      return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
    }
  }
  return null;
}

function decidingElims(game) {
  const dayIds = Array.isArray(game?.dayKills) ? game.dayKills : [];
  const nightIds = Array.isArray(game?.nightKills) ? game.nightKills : [];

  const dayAt = toMs(game?.lastKillAt);
  const nightAt = toMs(game?.nightResultAt);

  if (dayAt || nightAt) {
    if (!nightAt || (dayAt && dayAt > nightAt)) {
      return { when: "day", ids: dayIds };
    }
    return { when: "night", ids: nightIds };
  }

  if (nightIds.length) return { when: "night", ids: nightIds };
  if (dayIds.length) return { when: "day", ids: dayIds };

  const last = game?.lastKill ? [game.lastKill] : [];
  return { when: null, ids: last };
}

export default function WinnerOverlay({
  game,
  players,
  me,
  onLobby,
  loadingLobby,
  spectator,
}) {
  const winner = game?.winner || "";

  const playersById = React.useMemo(() => {
    const m = new Map();
    for (const p of players) m.set(p.id, p);
    return m;
  }, [players]);

  const byId = React.useCallback((id) => playersById.get(id), [playersById]);
  const nameOf = React.useCallback((id) => byId(id)?.name || "Unknown", [byId]);

  let explainer = "";

  if (winner === "Villains") {
    const { when, ids } = decidingElims(game);
    const victims = ids.map((id) => byId(id)).filter(Boolean);
    const victimsWithRoles = victims.map(nameWithRole);

    const verb =
      when === "day"
        ? "voted out"
        : when === "night"
        ? "killed last night"
        : "eliminated";

    if (victimsWithRoles.length === 1) {
      explainer = (
        <>
          {victimsWithRoles[0]} was {verb}.
          <br />
          Villains win!
        </>
      );
    } else if (victimsWithRoles.length >= 2) {
      explainer = (
        <>
          {joinList(victimsWithRoles)} were {verb}.
          <br />
          Villains win!
        </>
      );
    } else {
      explainer = "Villains win!";
    }
  } else if (winner === "Campers") {
    const { when, ids } = decidingElims(game);

    const villainVictims = ids
      .map((id) => byId(id))
      .filter((p) => (p?.team || "") === "Villain");

    const victimsWithRoles = villainVictims.map(nameWithRole);

    const verb =
      when === "day"
        ? "voted out"
        : when === "night"
        ? "killed last night"
        : "eliminated";

    if (victimsWithRoles.length === 1) {
      explainer = (
        <>
          {victimsWithRoles[0]} was {verb}.
          <br />
          Campers win!
        </>
      );
    } else if (victimsWithRoles.length >= 2) {
      explainer = (
        <>
          {joinList(victimsWithRoles)} were {verb}.
          <br />
          Campers win!
        </>
      );
    } else {
      // Fallback (should be rare)
      explainer = "Campers win!";
    }
  } else if (winner === "Emo") {
    const emoId = game?.uniqueWinner;
    const emoName =
      (emoId && nameOf(emoId)) ||
      players.find((p) => (p.roleTitle || "").toLowerCase() === "emo")?.name ||
      "The Emo";
    explainer = (
      <>
        {emoName} (Emo) was voted out.
        <br />
        Emo wins!
      </>
    );
  } else if (winner) {
    explainer = `${winner} win!`;
  }

  // ---- Winners list (primary + co-winners) ----
  const winners = React.useMemo(() => {
    if (!winner) return [];

    const lower = winner.toLowerCase();
    const active = players.filter((p) => p.isSpectator !== true);

    const uniqIds = Array.isArray(game?.uniqueWinners)
      ? new Set(game.uniqueWinners)
      : game?.uniqueWinner
      ? new Set([game.uniqueWinner])
      : new Set();

    const seen = new Set();
    const out = [];

    const add = (arr, p) => {
      if (!p) return;
      if (seen.has(p.id)) return;
      seen.add(p.id);
      arr.push({
        id: p.id,
        name: p.name || "Unknown",
        role: p.roleTitle || "",
        wins: typeof p.wins === "number" ? p.wins : 0,
      });
    };

    if (lower !== "campers" && lower !== "villains") {
      // UNIQUE primary (e.g., Emo): explicit IDs if provided; else role-title match
      let base = [];
      if (uniqIds.size > 0) {
        base = [...uniqIds].map((id) => byId(id)).filter(Boolean);
      } else {
        base = active.filter(
          (p) => (p.roleTitle || "").toLowerCase() === lower
        );
      }
      base.forEach((p) => add(out, p));

      // Anime Fans who rooted for one of the unique winners
      const winnerIdSet =
        base.length > 0 ? new Set(base.map((p) => p.id)) : new Set();
      active
        .filter((p) => (p.roleTitle || "").toLowerCase() === "anime fan")
        .forEach((fan) => {
          const rootedId = fan?.meta?.fanRootTarget;
          if (rootedId && winnerIdSet.has(rootedId)) add(out, fan);
        });
    } else {
      // TEAM primary
      const teamWanted = winner === "Villains" ? "Villain" : "Camper";

      // Team members
      active
        .filter((p) => (p.team || "") === teamWanted)
        .forEach((p) => add(out, p));

      // Anime Fans who rooted for that team (alive or dead)
      active
        .filter((p) => (p.roleTitle || "").toLowerCase() === "anime fan")
        .forEach((fan) => {
          const rootedId = fan?.meta?.fanRootTarget;
          const rooted = rootedId ? byId(rootedId) : null;
          if (rooted && (rooted.team || "") === teamWanted) add(out, fan);
        });
    }

    // Always merge any explicit unique winners (backend-provided), for either branch.
    if (uniqIds.size > 0) {
      [...uniqIds].forEach((id) => add(out, byId(id)));
    }

    // Hermit co-win: only when someone else wins (Campers, Villains, Emo).
    if (lower === "campers" || lower === "villains" || lower === "emo") {
      active
        .filter((p) => (p.roleTitle || "").toLowerCase() === "hermit")
        .forEach((hermit) => {
          const targetId = hermit?.meta?.hermitSabotageTarget;
          if (!targetId) return;
          const target = byId(targetId);
          if (target && target.alive === false) add(out, hermit);
        });
    }

    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [players, winner, game?.uniqueWinner, game?.uniqueWinners, byId]);

  const iWin = React.useMemo(() => {
    if (!winner || !me) return null;
    return winners.some((w) => w.id === me.id);
  }, [winner, me, winners]);
  const title = iWin == null ? "" : iWin ? "You win!" : "You lose!";

  return (
    <>
      <WinnerContainer>
        <WinnerWrapper>
          {title && !spectator && (
            <Header type="h1" align="center">
              {title}
            </Header>
          )}
          {explainer && (
            <Header type="h4" align="center" padding="0">
              {explainer}
            </Header>
          )}
          <ButtonsWrapper>
            <ButtonFilled
              disabled={loadingLobby}
              text={loadingLobby ? "Loading" : "Lobby"}
              onClick={onLobby}
            />
          </ButtonsWrapper>
        </WinnerWrapper>

        {winners.length > 0 && (
          <WinnerList>
            <Header type="h3" align="center" padding="0 0 8px">
              {winners.length > 1 ? "Winners" : "Winner"}
            </Header>
            <Body align="center">
              {winners.map((w) => {
                const suffix = w.wins > 0 ? ` (${w.wins})` : "";
                return (
                  <p key={w.id} style={{ padding: 4 }}>
                    <b>
                      {w.name}
                      {suffix}:
                    </b>{" "}
                    {w.role || "—"}
                  </p>
                );
              })}
            </Body>
          </WinnerList>
        )}
        <Donations />
      </WinnerContainer>
    </>
  );
}
